import React, { useCallback, useMemo } from "react";
import { Box, Button, useToast } from "@chakra-ui/react";
import { FormControlsTypeEnum } from "enums/form-controls-type.enum";
import * as Yup from "yup";
import {
  REQUIRED_FIELD,
  URL_FIELD,
  URL_FIELD_MISSING_PROTOCOL,
} from "constants/validator-messages";
import { AccountIntegrationsStore } from "store/UserAccounts/UserAccount/AccountIntegrations/AccountIntegrations.store";
import { AccountIntegrationTypeEnum } from "enums/account-integration-type.enum";
import { observer } from "mobx-react";
import {
  TAccountIntegrationData,
  TMRIRadarConfig,
} from "types/account-integration.type";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { ApiMessageStack, FormControlV2 } from "components";
import { doesUrlContainProtocol } from "utils/custom-yup-validators.utils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ToggleIntegrationActiveSwitchInput } from "../ToggleIntegrationActiveSwitchInput";

type TFieldValues = {
  mriRadarUrl: string;
  mriSonarAuthUrl: string;
  mriSonarUrl: string;
  active: boolean;
};

const validationSchema = Yup.object().shape({
  mriRadarUrl: Yup.string()
    .test("is-valid-url", URL_FIELD_MISSING_PROTOCOL, doesUrlContainProtocol)
    .url(URL_FIELD)
    .required(REQUIRED_FIELD),
  mriSonarAuthUrl: Yup.string()
    .test("is-valid-url", URL_FIELD_MISSING_PROTOCOL, doesUrlContainProtocol)
    .url(URL_FIELD)
    .required(REQUIRED_FIELD),
  mriSonarUrl: Yup.string()
    .test("is-valid-url", URL_FIELD_MISSING_PROTOCOL, doesUrlContainProtocol)
    .url(URL_FIELD)
    .required(REQUIRED_FIELD),

  active: Yup.boolean().required(REQUIRED_FIELD),
});

interface IProps {
  accountIntegrationsStore: AccountIntegrationsStore;
}

export const MRIRadarIntegrationForm: React.FC<IProps> = observer(
  ({ accountIntegrationsStore }) => {
    const toast = useToast();
    const accountIntegration = accountIntegrationsStore
      .accountIntegrationsMapByType[
      AccountIntegrationTypeEnum.MRI_RADAR
    ] as TAccountIntegrationData<TMRIRadarConfig>;

    const initialValues: TFieldValues = useMemo(
      () => ({
        mriRadarUrl: accountIntegration.integrationConfig.mriRadarUrl,
        mriSonarAuthUrl:
          accountIntegration.integrationConfig.mriSonarAuthUrl ?? "",
        mriSonarUrl: accountIntegration.integrationConfig.mriSonarUrl ?? "",
        active: accountIntegration.active,
      }),
      [accountIntegration]
    );

    const {
      handleSubmit,
      watch,
      control,
      formState: { isSubmitting },
    } = useForm<TFieldValues>({
      defaultValues: initialValues,
      mode: "onSubmit",
      resolver: yupResolver(validationSchema),
    });

    const { active } = watch();

    const onSubmit = useCallback(
      async ({
        mriRadarUrl,
        mriSonarAuthUrl,
        mriSonarUrl,
        active,
      }: TFieldValues) => {
        try {
          await accountIntegrationsStore.updateAccountIntegration<
            TMRIRadarConfig
          >(accountIntegration.id, {
            type: accountIntegration.type,
            active: active,
            integrationConfig: {
              mriRadarUrl,
              mriSonarAuthUrl,
              mriSonarUrl,
            },
          });
          toast({
            ...DEFAULT_SUCCESS_TOAST_OPTIONS,
            description: (
              <ApiMessageStack
                messageStack={"MRI Radar integration configuration updated."}
              />
            ),
          });
        } catch (error) {
          toast({
            ...DEFAULT_ERROR_TOAST_OPTIONS,
            description: <ApiMessageStack messageStack={error.message} />,
          });
        }
      },
      [
        accountIntegration.id,
        accountIntegration.type,
        accountIntegrationsStore,
        toast,
      ]
    );

    return (
      <Box w={"100%"}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ToggleIntegrationActiveSwitchInput<TFieldValues>
            label={"Connecting with MRI Radar"}
            value={active}
            name={"active"}
            control={control}
          />
          <Box mb={5}>
            <FormControlV2<TFieldValues>
              name={"mriRadarUrl"}
              control={control}
              label={"MRI Radar Webhook URL"}
              type={FormControlsTypeEnum.TEXT}
              additionalProps={{
                placeholder: "MRI Radar Webhook URL",
              }}
            />
          </Box>

          <Box mb={5}>
            <FormControlV2<TFieldValues>
              name={"mriSonarAuthUrl"}
              control={control}
              label={"MRI Sonar Base Auth URL"}
              type={FormControlsTypeEnum.TEXT}
              additionalProps={{
                placeholder: "MRI Sonar Base Auth URL",
              }}
            />
          </Box>

          <Box mb={5}>
            <FormControlV2<TFieldValues>
              name={"mriSonarUrl"}
              control={control}
              label={"MRI Sonar Base URL"}
              type={FormControlsTypeEnum.TEXT}
              additionalProps={{
                placeholder: "MRI Sonar Base URL",
              }}
            />
          </Box>
          <Box
            mb={2}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
          ></Box>

          <Box
            width={"100%"}
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"flex-end"}
          >
            <Button
              type={"submit"}
              colorScheme={"blue"}
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Update
            </Button>
          </Box>
        </form>
      </Box>
    );
  }
);
